import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  applyJourneyCreateSetup,
  CAPTURE_INTENT_OPTIONS,
  createDefaultJourneyCreateSetup,
  createEmptyForm,
  createEmptyTracker,
  createLeadCaptureTracker,
  patchJourneyCreateSetup,
  withProgrammePublicRules,
} from "./trackerStorage";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const CaptureCard = ({ option, onClick }) => {
  const card = (
    <Box
      onClick={option.available ? onClick : undefined}
      sx={{
        border: `1.5px solid ${BORDER}`,
        bgcolor: "#fff",
        borderRadius: "10px",
        px: { xs: 2, md: 2.5 },
        py: { xs: 2, md: 2.25 },
        cursor: option.available ? "pointer" : "not-allowed",
        height: "100%",
        minHeight: { xs: 110, md: 132 },
        transition: "all 0.15s ease",
        position: "relative",
        "&:hover": option.available
          ? {
              borderColor: PRIMARY,
              bgcolor: PRIMARY_SOFT,
              boxShadow: `0 0 0 1px ${PRIMARY}`,
            }
          : undefined,
      }}
    >
      {!option.available && (
        <Typography
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color: PRIMARY,
            bgcolor: PRIMARY_SOFT,
            border: `1.5px solid ${PRIMARY}`,
            px: 1,
            py: 0.35,
            borderRadius: "6px",
            lineHeight: 1.2,
          }}
        >
          Coming soon
        </Typography>
      )}
      <Typography
        sx={{
          fontWeight: 700,
          color: "#0f172a",
          fontSize: { xs: 15, md: 16 },
          pr: 7,
          lineHeight: 1.3,
        }}
      >
        {option.title}
      </Typography>
      <Typography
        sx={{
          color: MUTED,
          lineHeight: 1.45,
          fontSize: { xs: 13, md: 13.5 },
          mt: 0.75,
        }}
      >
        {option.description}
      </Typography>
    </Box>
  );

  if (option.available) return card;

  return (
    <Tooltip title="Coming soon" arrow placement="top">
      <Box sx={{ height: "100%" }}>{card}</Box>
    </Tooltip>
  );
};

/** In-memory draft only — not written to storage until basics are saved. */
export const buildDraftFromIntent = (intentOrOption) => {
  const blank =
    intentOrOption === "blank" ||
    intentOrOption?.blank === true ||
    intentOrOption?.id === "blank";

  const lead =
    intentOrOption === "lead" ||
    intentOrOption?.id === "lead" ||
    intentOrOption?.category === "lead_capture";

  let tracker = {
    ...createEmptyTracker(),
    status: "draft",
    name: "",
    description: "",
  };

  if (blank) {
    return withProgrammePublicRules({
      ...tracker,
      questionPattern: "one_form_once",
      forms: [
        createEmptyForm({
          id: `form_main_${Date.now()}`,
          name: "Main form",
          role: "first_submission",
          shortDescription: "Nothing preset — add your questions.",
          nextFormId: "end",
          questions: [],
        }),
      ],
      createSetup: null,
      programmes: [],
    });
  }

  if (lead) {
    return withProgrammePublicRules({
      ...createLeadCaptureTracker(),
      status: "draft",
      name: "",
      description: "",
    });
  }

  // New journeys start with no scheduled coach reviews (type None); weeks,
  // days or dates are only added when the admin adds them. The duration period
  // starts empty (""): the admin picks the weeks.
  return patchJourneyCreateSetup(
    applyJourneyCreateSetup(
      tracker,
      { ...createDefaultJourneyCreateSetup(), durationWeeks: "" },
      []
    ),
    {
      scheduledReviews: "none",
      alsoReviewEveryCheckin: true,
      coachReviewWeeks: [],
      coachReviewDays: [],
      coachReviewFixedDates: [],
    }
  );
};

export default function CreateTrackerIntentPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const handlePickCapture = (option) => {
    if (!option.available) {
      enqueueSnackbar("Coming soon — Journey is available now", {
        variant: "info",
      });
      return;
    }
    // No save yet — builder collects basics first.
    const routeIntent =
      option.id === "lead" ? "lead" : option.blank ? "blank" : "journey";
    navigate(`/trackers/new/${routeIntent}`);
  };

  return (
    <div className="container-fluid tracker-module-page tracker-builder-page">
      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          alignItems: "flex-start",
          mb: 2.5,
          width: "100%",
        }}
      >
        <IconButton
          size="small"
          onClick={() => navigate("/trackers")}
          sx={{ mt: 0.35 }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 22, md: 26 },
              color: "#0f172a",
              lineHeight: 1.2,
            }}
          >
            What do you want to capture?
          </Typography>
          <Typography
            sx={{ color: MUTED, mt: 0.6, fontSize: 14, lineHeight: 1.45 }}
          >
            Pick one. Next you set the basics — nothing is saved until you name
            it and save.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
          },
          gap: { xs: 1.5, md: 2 },
          width: "100%",
          alignContent: "start",
          pb: 3,
        }}
      >
        {CAPTURE_INTENT_OPTIONS.map((option) => (
          <CaptureCard
            key={option.id}
            option={option}
            onClick={() => handlePickCapture(option)}
          />
        ))}
      </Box>
    </div>
  );
}
